import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { mapLeft } from '@dev-stream/utils';
import { BillingPaymentDetailVM } from '@idocs/api/business/models/BillingPaymentDetailVM.model';
import { BillingPaymentApiService } from '@idocs/api/business/services/BillingPayment.api-service';
import { Disposable } from '@idocs/shared-logic';
import { showNotification } from '@idocs/shared-ui/notification';
import { PaginatorConfig } from '@idocs/shared-ui/paginator/paginator/paginator.component';
import {
    PaymentsHistoryInfo,
    PaymentStatus,
    PaymentType,
} from 'libraries/ui/billing-ui/src/lib/components/payments-history/payments-history.component';
import * as moment from 'moment';
import { notNull } from 'ng-utils/utils';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { apiResponseErrorHandler } from '../../utils/handlers/api-error-handler';
import { PaymentService } from './payment.service';

@Injectable({ providedIn: 'root' })
export class PaymentsHistoryService extends Disposable {
    private _paymentsHistory = new ReplaySubject<PaymentsHistoryInfo[] | null>();

    description =
        'Счета со статусом “Ожидает оплаты” возможно оплатить карточкой, нажав на кнопку “Оплатить картой”. Для оплаты счетов через банковский перевод скачайте счет, нажав на название счета.';
    paginator = new PaginatorConfig();

    get paymentsHistory() {
        return this._paymentsHistory.asObservable();
    }

    constructor(private billingPaymentApi: BillingPaymentApiService, private paymentService: PaymentService) {
        super();
    }

    async getData(count: number | null = null) {
        this._paymentsHistory.next(null);

        const skip = this.paginator.pageSize * this.paginator.pageIndex;

        const res = await this.billingPaymentApi
            .Get_by$select_by$expand_by$filter_by$orderby_by$top_by$skip_by$count({
                $orderby: 'PaymentDate desc, Status desc',
                ...(!count
                    ? {
                          $top: this.paginator.pageSize,
                          $skip: skip,
                          $count: true,
                      }
                    : {
                          $top: count,
                      }),
            })
            .pipe(takeUntil(this.destroyed), mapLeft(apiResponseErrorHandler), mapLeft(showNotification))
            .toPromise();

        if (res.isLeft()) {
            return;
        }

        const payments = res.value.value;

        if (payments && payments.length > 0) {
            const paymentsHistoryInfos = payments.map((p) => {
                const sortedDetails = p.BillingPaymentDetails.filter(notNull).sort(this.billingPaymentDetailComparer);
                let details = null;
                if (sortedDetails && sortedDetails.length > 0) {
                    details = sortedDetails[0];
                }

                return new PaymentsHistoryInfo({
                    Id: p.Id,
                    Name: p.Name,
                    PaymentDate: moment(moment(p.CreatedOn).utc()).local(true).toDate(),
                    Status: p.Status,
                    TotalAmount: p.TotalAmount,
                    CompanyId: p.Company?.Id,
                    InvoiceId: p.InvoiceId,
                    PaymentType: p.Status != PaymentStatus.NEW ? details?.Type : null,
                    CardMask:
                        p.Status != PaymentStatus.NEW
                            ? details?.Type == PaymentType.CARD
                                ? details?.BillingBankCard?.CardMask
                                : null
                            : null,
                });
            });

            this._paymentsHistory.next(paymentsHistoryInfos);

            if (!count) {
                this.paginator.length = res.value['@odata.count'] || 0;
            }
        } else {
            this._paymentsHistory.next([]);
            this.paginator.length = 0;
        }
    }

    private billingPaymentDetailComparer(a: BillingPaymentDetailVM, b: BillingPaymentDetailVM): number {
        return (b.CreatedOn || new Date()).getTime() - (a.CreatedOn || new Date()).valueOf();
    }

    async payByCard(model: PaymentsHistoryInfo) {
        return await this.paymentService.selectCard(model);
    }

    async pageChanged($event: PageEvent) {
        this.paginator.pageIndex = $event.pageIndex;
        this.paginator.pageSize = $event.pageSize;
        await this.getData();
    }
}
